<script setup lang="ts">
import type { Schemas } from "#shopware";
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware-pwa/helpers-next";

const { count } = useCart();
const { count: wishlistCount } = useWishlist();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const navigationElements = inject("swNavigation-service-navigation");

const filteredNavigations = computed(() => {
  if (navigationElements) {
    const navigation: Schemas["Category"][] = JSON.parse(
      JSON.stringify(navigationElements.value)
    );
    return navigation;
  }
  return [];
});

const miniCartModal = useMiniCartModal();

const emit = defineEmits(["toggleMobileMenu"]);
const searchModal = ref(false);
const scrollPosition = ref(0);

onMounted(() => {
  window.addEventListener('scroll', () => {
    scrollPosition.value = window.scrollY;
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', () => {
    scrollPosition.value = window.scrollY;
  });
});
</script>

<template>
  <!-- Right Section: Icons and Mobile Menu Button -->
  <div class="flex items-center space-x-4">
    <!-- Language Dropdown -->
    <button
      class="text-black bg-transparent border-none md:hidden"
      aria-label="Search"
      data-testid="search-button"
      @click="searchModal = true"
    >
      <img
        src="assets/images/search-icon-white.svg"
        alt="search-icon"
        class="w-5 h-5 sm:w-5 sm:h-5"
      />
    </button>

    <teleport to="body">
      <div 
        v-if="searchModal"
        class="fixed z-50 top-0 left-0 w-full h-[100%] bg-black bg-opacity-20 px-2 md:hidden transition-[padding] duration-300 ease-in-out" 
        :style="{ paddingTop: scrollPosition < 50 ? '110px' : '75px' }"
        @click="searchModal = false"
      >
        <LayoutStoreSearch @link-clicked="searchModal = false" @click.stop="" :mobile="true" />
      </div>
    </teleport>
    
    <LanguageSwitcher />

    <!-- User, Like, Cart Icons -->
    <div class="product-sec flex items-center space-x-4">
      <div class="min-w-[20px] h-[20px] w-[20px]">
        <client-only>
          <AccountMenu />
          <template #fallback>
            <div class="animate-pulse bg-gray-200 w-5 h-5 rounded-full"></div>
          </template>
        </client-only>
      </div>

      <NuxtLink
        class="text-black relative"
        aria-label="wishlist"
        data-testid="wishlist-button"
        :to="formatLink(`/wishlist`)"
      >
        <img
          src="assets/images/like-product.svg"
          alt="like-product"
          class="w-5 h-5 sm:w-5 sm:h-5"
        />
        <client-only>
          <span
            v-if="wishlistCount"
            class="w-4 h-4 rounded-full font-semibold text-[10px] bg-red-bg font-inter flex items-center justify-center text-black02 absolute right-[-10px] top-[-7px]"
            >{{ wishlistCount }}</span
          >
        </client-only>
      </NuxtLink>

      <button
        class="text-black bg-transparent border-none relative"
        data-testid="cart-button"
        aria-label="Mini cart"
        @click="miniCartModal.open"
      >
        <img
          src="assets/images/shopping-cart.svg"
          alt="cart-icon"
          class="w-6 h-6 sm:w-6 sm:h-6"
        />

        <span
          v-if="count"
          class="w-4 h-4 text-white rounded-full font-semibold text-[10px] bg-red-bg font-inter flex items-center justify-center text-black02 absolute right-[-10px] -top-[7px]"
          >{{ count || "" }}</span
        >
      </button>
    </div>

    <!-- Mobile Menu Toggle (Visible in Mobile View) -->
    <button
      id="mobileMenuButton"
      class="block lg:hidden bg-transparent border-none text-white"
      @click="emit('toggleMobileMenu')"
    >
      <img
        src="assets/images/menu-icon.svg"
        alt="menu-icon"
        class="w-5 h-5 sm:w-5 sm:h-5"
      />
    </button>
  
  </div>
</template>
